import { useState, useEffect, useCallback } from "react";
import {
  Spinner,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconMessageCircle,
  IconClock,
  IconPlus,
  IconTrash,
  IconRefresh,
  IconInfoCircle,
  IconCheck,
  IconAlertTriangle,
  IconSparkles,
  IconSend,
} from "@tabler/icons-react";
import styled from "styled-components";
import Swal from "sweetalert2";
import { useAgentHubContext } from "../contexts/AgentHubContext";
import LeadFollowupService, { IntervalHelpers } from "../../../services/LeadFollowupServices";

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const HeaderIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  color: var(--text-primary);
  font-weight: 700;
  font-size: 28px;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 15px;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  background: ${(props) => (props.active ? "#dcfce7" : "#f3f4f6")};
  color: ${(props) => (props.active ? "#16a34a" : "#6b7280")};
  margin-top: 12px;
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: var(--shadow-md);
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const SectionIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${(props) => props.bg || "var(--primary-100)"};
  color: ${(props) => props.color || "var(--primary)"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: ${(props) =>
    props.active ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" : "#f9fafb"};
  border-radius: 12px;
  border: 2px solid ${(props) => (props.active ? "#86efac" : "#e5e7eb")};
  transition: all 0.3s ease;
`;

const ToggleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleLabel = styled.div`
  h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    margin: 0;
    font-size: 13px;
    color: var(--text-secondary);
  }
`;

const CustomSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 56px;
  height: 30px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e1;
    transition: 0.3s;
    border-radius: 30px;

    &:before {
      position: absolute;
      content: "";
      height: 24px;
      width: 24px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }
  }

  input:checked + span {
    background: var(--gradient-primary);
  }

  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const PresetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PresetCard = styled.button`
  padding: 16px;
  background: ${(props) =>
    props.active
      ? "linear-gradient(135deg, var(--primary) 0%, #6366f1 100%)"
      : "white"};
  color: ${(props) => (props.active ? "white" : "var(--text-primary)")};
  border: 2px solid ${(props) => (props.active ? "var(--primary)" : "var(--border-color)")};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: var(--primary);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  h5 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 600;
  }

  small {
    font-size: 11px;
    opacity: ${(props) => (props.active ? 0.9 : 0.7)};
  }
`;

const IntervalsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IntervalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    border-color: var(--primary-200);
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const IntervalNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const IntervalInput = styled.input`
  width: 80px;
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-100);
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const UnitSelect = styled.select`
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const IntervalDisplay = styled.span`
  flex: 1;
  color: var(--text-secondary);
  font-size: 13px;

  @media (max-width: 768px) {
    width: 100%;
    order: 3;
    margin-top: 8px;
  }
`;

const RemoveButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: #fee2e2;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #fecaca;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddIntervalButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: transparent;
  color: var(--primary);
  border: 2px dashed var(--primary-200);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MessageLimitDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  border: 2px solid #fcd34d;
`;

const MessageLimitInfo = styled.div`
  p {
    margin: 0 0 4px 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
  }

  small {
    color: #92400e;
    font-size: 13px;
  }
`;

const MessageLimitValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .value {
    font-size: 32px;
    font-weight: 700;
    color: #d97706;
    line-height: 1;
  }

  .label {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 4px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 16px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-100);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 8px;
`;

const SaveContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 32px;
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  background: white;
  color: var(--text-secondary);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--text-secondary);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  color: var(--text-secondary);
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #eff6ff;
  border-radius: 12px;
  border: 1px solid #bfdbfe;
  margin-bottom: 20px;

  svg {
    flex-shrink: 0;
    color: #3b82f6;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.5;
  }
`;

const DisabledOverlay = styled.div`
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  transition: opacity 0.3s ease;
`;

// ============================================
// COMPONENT
// ============================================

const LeadFollowup = () => {
  const { agentId } = useAgentHubContext();

  // Estados
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Estados do formulário
  const [enabled, setEnabled] = useState(false);
  const [intervals, setIntervals] = useState([
    { value: 2, unit: "hours", minutes: 120 },
  ]);
  const [businessContext, setBusinessContext] = useState("");
  const [activePreset, setActivePreset] = useState(null);

  // Carregar configuração
  const loadConfig = useCallback(async () => {
    if (!agentId) return;

    setLoading(true);
    try {
      const data = await LeadFollowupService.getConfig(agentId);
      setConfig(data);

      // Preencher estados com dados da API
      setEnabled(data.enabled || false);
      setBusinessContext(data.business_context || "");

      // Converter intervalos de minutos para formato editável
      if (data.intervals && data.intervals.length > 0) {
        const convertedIntervals = data.intervals.map((min) => {
          const { value, unit } = IntervalHelpers.minutesToBestUnit(min);
          return { value, unit, minutes: min };
        });
        setIntervals(convertedIntervals);
      }

      // Verificar se corresponde a algum preset
      checkActivePreset(data.intervals || []);
    } catch (error) {
      console.error("Erro ao carregar configuração:", error);
      Swal.fire({
        icon: "error",
        title: "Erro ao carregar",
        text: "Não foi possível carregar as configurações de follow-up.",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Verificar mudanças
  useEffect(() => {
    if (!config) return;

    const currentIntervals = intervals.map((i) => i.minutes).sort((a, b) => a - b);
    const originalIntervals = (config.intervals || []).sort((a, b) => a - b);

    const hasIntervalChanges =
      JSON.stringify(currentIntervals) !== JSON.stringify(originalIntervals);
    const hasOtherChanges =
      enabled !== config.enabled ||
      businessContext !== (config.business_context || "");

    setHasChanges(hasIntervalChanges || hasOtherChanges);
  }, [enabled, intervals, businessContext, config]);

  // Verificar preset ativo
  const checkActivePreset = (intervalMinutes) => {
    const sorted = [...intervalMinutes].sort((a, b) => a - b);

    for (const [key, preset] of Object.entries(IntervalHelpers.presets)) {
      const presetSorted = [...preset.intervals].sort((a, b) => a - b);
      if (JSON.stringify(sorted) === JSON.stringify(presetSorted)) {
        setActivePreset(key);
        return;
      }
    }
    setActivePreset(null);
  };

  // Aplicar preset
  const applyPreset = (presetKey) => {
    const preset = IntervalHelpers.presets[presetKey];
    if (!preset) return;

    const convertedIntervals = preset.intervals.map((min) => {
      const { value, unit } = IntervalHelpers.minutesToBestUnit(min);
      return { value, unit, minutes: min };
    });

    setIntervals(convertedIntervals);
    setActivePreset(presetKey);
  };

  // Obter valor máximo baseado na unidade (7 dias = 10080 minutos)
  const getMaxValueForUnit = (unit) => {
    switch (unit) {
      case 'days':
        return 7;
      case 'hours':
        return 168; // 7 * 24
      case 'minutes':
      default:
        return 10080; // 7 * 24 * 60
    }
  };

  // Atualizar intervalo
  const updateInterval = (index, field, value) => {
    const newIntervals = [...intervals];
    newIntervals[index][field] = value;

    // Recalcular minutos e aplicar limite de 7 dias
    if (field === "value" || field === "unit") {
      let numValue = parseInt(newIntervals[index].value) || 0;
      const unit = newIntervals[index].unit;
      const maxValue = getMaxValueForUnit(unit);

      // Limitar valor ao máximo permitido para a unidade
      if (numValue > maxValue) {
        numValue = maxValue;
        newIntervals[index].value = maxValue;
      }

      let minutes = IntervalHelpers.toMinutes(numValue, unit);

      // Garantir que não exceda 7 dias (10080 minutos)
      if (minutes > 10080) {
        minutes = 10080;
        const { value: newVal, unit: newUnit } = IntervalHelpers.minutesToBestUnit(10080);
        newIntervals[index].value = newVal;
        newIntervals[index].unit = newUnit;
      }

      newIntervals[index].minutes = minutes;
    }

    setIntervals(newIntervals);
    setActivePreset(null);
  };

  // Adicionar intervalo
  const addInterval = () => {
    if (intervals.length >= 7) {
      Swal.fire({
        icon: "warning",
        title: "Limite atingido",
        text: "Você pode adicionar no máximo 7 intervalos de follow-up.",
        confirmButtonColor: "var(--primary)",
      });
      return;
    }

    // Sugerir próximo intervalo baseado no último
    const lastInterval = intervals[intervals.length - 1];
    let newMinutes = lastInterval.minutes * 2;
    if (newMinutes > 10080) newMinutes = 10080;

    const { value, unit } = IntervalHelpers.minutesToBestUnit(newMinutes);

    setIntervals([...intervals, { value, unit, minutes: newMinutes }]);
    setActivePreset(null);
  };

  // Remover intervalo
  const removeInterval = (index) => {
    if (intervals.length <= 1) {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "É necessário manter pelo menos um intervalo.",
        confirmButtonColor: "var(--primary)",
      });
      return;
    }

    const newIntervals = intervals.filter((_, i) => i !== index);
    setIntervals(newIntervals);
    setActivePreset(null);
  };

  // Validar dados antes de salvar
  const validateData = () => {
    const errors = [];

    // Validar intervalos
    for (let i = 0; i < intervals.length; i++) {
      const validation = IntervalHelpers.validateInterval(intervals[i].minutes);
      if (!validation.valid) {
        errors.push(`Intervalo ${i + 1}: ${validation.error}`);
      }
    }

    // Verificar duplicatas
    const uniqueMinutes = new Set(intervals.map((i) => i.minutes));
    if (uniqueMinutes.size !== intervals.length) {
      errors.push("Existem intervalos duplicados");
    }

    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Dados inválidos",
        html: errors.map((e) => `<p style="margin: 4px 0;">• ${e}</p>`).join(""),
        confirmButtonColor: "var(--primary)",
      });
      return false;
    }

    return true;
  };

  // Salvar configuração
  const handleSave = async () => {
    if (!validateData()) return;

    setSaving(true);
    try {
      const payload = {
        enabled,
        intervals: intervals.map((i) => i.minutes).sort((a, b) => a - b),
        max_messages: intervals.length, // Calculado automaticamente pelo número de intervalos
        business_context: businessContext.trim() || null,
      };

      const updatedConfig = await LeadFollowupService.updateConfig(agentId, payload);
      setConfig(updatedConfig);
      setHasChanges(false);

      Swal.fire({
        icon: "success",
        title: "Configurações salvas!",
        text: enabled
          ? "O follow-up automático está ativo para este agente."
          : "As configurações foram salvas. Ative quando estiver pronto.",
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "bottom-end",
        timerProgressBar: true,
      });
    } catch (error) {
      console.error("Erro ao salvar:", error);

      let errorMessage = "Não foi possível salvar as configurações.";

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === "string") {
          errorMessage = data;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          errorMessage = data.detail;
        }
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao salvar",
        text: errorMessage,
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setSaving(false);
    }
  };

  // Resetar para valores originais
  const handleReset = () => {
    if (!config) return;

    Swal.fire({
      title: "Descartar alterações?",
      text: "Todas as mudanças não salvas serão perdidas.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, descartar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
    }).then((result) => {
      if (result.isConfirmed) {
        loadConfig();
      }
    });
  };

  // Renderização
  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p>Carregando configurações...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderIcon>
          <IconMessageCircle size={28} />
        </HeaderIcon>
        <HeaderContent>
          <Title>Follow-up de Leads</Title>
          <Subtitle>
            Configure mensagens automáticas para leads que não respondem.
            O agente enviará mensagens de reengajamento nos intervalos definidos.
          </Subtitle>
          <StatusBadge active={enabled}>
            {enabled ? (
              <>
                <IconCheck size={16} />
                Follow-up Ativo
              </>
            ) : (
              <>
                <IconAlertTriangle size={16} />
                Follow-up Desativado
              </>
            )}
          </StatusBadge>
        </HeaderContent>
      </Header>

      {/* Toggle de Ativação */}
      <Section>
        <ToggleContainer active={enabled}>
          <ToggleInfo>
            <SectionIcon bg={enabled ? "#dcfce7" : "#f3f4f6"} color={enabled ? "#16a34a" : "#6b7280"}>
              <IconSend size={20} />
            </SectionIcon>
            <ToggleLabel>
              <h4>{enabled ? "Follow-up Ativado" : "Follow-up Desativado"}</h4>
              <p>
                {enabled
                  ? "Mensagens automáticas serão enviadas para leads inativos"
                  : "Ative para começar a enviar mensagens de follow-up"}
              </p>
            </ToggleLabel>
          </ToggleInfo>
          <CustomSwitch>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span />
          </CustomSwitch>
        </ToggleContainer>
      </Section>

      {/* Configurações (com overlay quando desativado) */}
      <DisabledOverlay disabled={!enabled}>
        {/* Presets de Intervalos */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon bg="#f0fdf4" color="#16a34a">
                <IconSparkles size={18} />
              </SectionIcon>
              Modelos Pré-definidos
            </SectionTitle>
          </SectionHeader>

          <InfoBox>
            <IconInfoCircle size={20} />
            <p>
              Escolha um modelo para aplicar intervalos pré-configurados, ou personalize
              os intervalos manualmente abaixo.
            </p>
          </InfoBox>

          <PresetsContainer>
            {Object.entries(IntervalHelpers.presets).map(([key, preset]) => (
              <PresetCard
                key={key}
                active={activePreset === key}
                onClick={() => applyPreset(key)}
                disabled={!enabled}
              >
                <h5>{preset.name}</h5>
                <small>{preset.description}</small>
              </PresetCard>
            ))}
          </PresetsContainer>
        </Section>

        {/* Configuração de Intervalos */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon bg="#eff6ff" color="#3b82f6">
                <IconClock size={18} />
              </SectionIcon>
              Intervalos de Envio
            </SectionTitle>
            <Badge bg="secondary" pill>
              {intervals.length} de 7
            </Badge>
          </SectionHeader>

          <InfoBox>
            <IconInfoCircle size={20} />
            <p>
              Defina os intervalos de tempo após a última mensagem do lead para enviar
              cada follow-up. Os intervalos são aplicados sequencialmente.
            </p>
          </InfoBox>

          <IntervalsGrid>
            {intervals.map((interval, index) => (
              <IntervalRow key={index}>
                <IntervalNumber>{index + 1}</IntervalNumber>
                <IntervalInput
                  type="number"
                  min="1"
                  max={getMaxValueForUnit(interval.unit)}
                  value={interval.value}
                  onChange={(e) =>
                    updateInterval(index, "value", parseInt(e.target.value) || 1)
                  }
                  disabled={!enabled}
                />
                <UnitSelect
                  value={interval.unit}
                  onChange={(e) => updateInterval(index, "unit", e.target.value)}
                  disabled={!enabled}
                >
                  <option value="minutes">minutos</option>
                  <option value="hours">horas</option>
                  <option value="days">dias</option>
                </UnitSelect>
                <IntervalDisplay>
                  = {IntervalHelpers.minutesToDisplay(interval.minutes)} após inatividade
                </IntervalDisplay>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Remover intervalo</Tooltip>}
                >
                  <RemoveButton
                    onClick={() => removeInterval(index)}
                    disabled={!enabled || intervals.length <= 1}
                  >
                    <IconTrash size={16} />
                  </RemoveButton>
                </OverlayTrigger>
              </IntervalRow>
            ))}
          </IntervalsGrid>

          <AddIntervalButton
            onClick={addInterval}
            disabled={!enabled || intervals.length >= 7}
          >
            <IconPlus size={18} />
            Adicionar Intervalo
          </AddIntervalButton>
        </Section>

        {/* Máximo de Mensagens (calculado automaticamente) */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon bg="#fef3c7" color="#d97706">
                <IconMessageCircle size={18} />
              </SectionIcon>
              Limite de Mensagens
            </SectionTitle>
          </SectionHeader>

          <MessageLimitDisplay>
            <MessageLimitInfo>
              <p>Máximo de mensagens por lead</p>
              <small>
                Calculado automaticamente com base nos intervalos configurados
              </small>
            </MessageLimitInfo>
            <MessageLimitValue>
              <span className="value">{intervals.length}</span>
              <span className="label">{intervals.length === 1 ? 'mensagem' : 'mensagens'}</span>
            </MessageLimitValue>
          </MessageLimitDisplay>
        </Section>

        {/* Contexto do Negócio */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon bg="#fae8ff" color="#a855f7">
                <IconInfoCircle size={18} />
              </SectionIcon>
              Contexto do Negócio
            </SectionTitle>
            <Badge bg="light" text="muted">
              Opcional
            </Badge>
          </SectionHeader>

          <InfoBox>
            <IconInfoCircle size={20} />
            <p>
              Descreva brevemente seu negócio para que as mensagens de follow-up
              sejam mais personalizadas e relevantes para seus leads.
            </p>
          </InfoBox>

          <TextArea
            placeholder="Ex: Clínica de estética especializada em harmonização facial e procedimentos minimamente invasivos. Atendemos principalmente mulheres de 25 a 50 anos que buscam realçar a beleza natural. ***ATENÇÃO*** Não insira prompt para o agente nesta etapa! Apenas o contexto do negócio."
            value={businessContext}
            onChange={(e) => setBusinessContext(e.target.value)}
            maxLength={500}
            disabled={!enabled}
          />
          <CharCount>{businessContext.length}/500 caracteres</CharCount>
        </Section>
      </DisabledOverlay>

      {/* Botões de Ação */}
      <SaveContainer>
        <ResetButton onClick={handleReset} disabled={!hasChanges || saving}>
          <IconRefresh size={18} />
          Descartar Alterações
        </ResetButton>
        <SaveButton onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" />
              Salvando...
            </>
          ) : (
            <>
              <IconCheck size={18} />
              Salvar Configurações
            </>
          )}
        </SaveButton>
      </SaveContainer>
    </Container>
  );
};

export default LeadFollowup;
